import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import format from 'date-fns/format';

import ZRMUtils from '@zg-rentals/zrm-utils-js/lib/utils';
import { Button, Flex } from '@zillow/constellation';
import { Container, Tag } from '@zillow/zrs-comet';

import {
    AppSelectors, RoutingSelectors, PropertiesSelectors,
} from 'app/shared/selectors';
import { STATUS_KEY, getTagTypeByKey } from 'app/shared/utils/statusTagUtils';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import accessUtils from 'app/shared/utils/accessUtils';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import ModalActions from 'app/shared/actions/ModalActions';
import ModalConstants from 'app/shared/constants/ModalConstants';
import PaidInclusionConstants from 'app/shared/constants/PaidInclusionConstants';
import PostingActions from 'app/shared/actions/PostingActions';
import PropertiesActions from 'app/shared/actions/PropertiesActions';
import PropertyConstants from 'app/shared/constants/PropertyConstants';
import zGuaranteeIcon from 'app/shared/images/z-guarantee.svg';
import useVisibility from 'app/shared/hooks/useVisibility';

import AdditionalInformation from './components/AdditionalInformation';
import FeedConnectAdditionalInformation from './components/FeedConnectAdditionalInformation';
import ErrorMessage from './components/ErrorMessage';
import LearnMore from './components/LearnMore';

import {
    StickyListingFooterContainer,
    StyledFlexContainer,
    StyledGuaranteeIcon,
    StyledMiddleFlex,
    StyledTagSpacer,
} from './StyledComponents';

const {
    makeAppConfig,
    makeCookieEnabledServices,
    makeIsScreenConstellationMobile,
    makeIsScreenConstellationTablet,
} = AppSelectors;
const { SYNDICATION_OVERVIEW_STATUS, LISTING_STATUS } = PropertyConstants;
const { GA_EVENTS, SUFFIX } = AnalyticsConstants;
const { appendSuffix, track } = analyticsUtils;
const { DEACTIVATE_OPTION } = PaidInclusionConstants;

const propTypes = {
    canAccessPayments: PropTypes.bool.isRequired,
    formData: PropTypes.object.isRequired,
    hasRestrictionErrorMessage: PropTypes.bool.isRequired,
    onPublishClick: PropTypes.func.isRequired,
    paidListingExists: PropTypes.bool,
    updateFormData: PropTypes.func.isRequired,
};

const defaultProps = {
    paidListingExists: false,
};

const ConnectedListingFooter = ({
    canAccessPayments,
    formData: {
        active,
        aliasEncoded,
        expirationDate,
        isActivationBlocked,
        isFeed,
        isCommunity,
        isPaidMultifamily,
        singleFamilyContract,
        seattleHousingConnectorStatus,
        paidInclusion: {
            isEnabled: isInPaidMarket = null,
            isPaymentRequired = null,
            price = null,
        } = {},
        propertyType,
        syndication,
    },
    hasRestrictionErrorMessage,
    onPublishClick,
    paidListingExists,
    updateFormData,
}) => {
    const dispatch = useDispatch();
    const appConfig = useSelector(state => makeAppConfig(state));
    const cookieEnabledServices = useSelector(state => makeCookieEnabledServices(state));
    const isScreenMobile = useSelector(state => makeIsScreenConstellationMobile(state));
    const isScreenTablet = useSelector(state => makeIsScreenConstellationTablet(state));
    const currentProperty = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));

    const {
        canUpdateActiveStatus,
        leadGuarantee: {
            isEligible: isEligibleForLeadGuarantee,
        },
    } = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));

    const search = useSelector(state => RoutingSelectors.makeSearch(state));

    const [isVisible, isVisibleRef] = useVisibility(-50, 200);

    useEffect(() => {
        checkQueryParamActions();
    }, []);

    const canFeedConnectCreateListing =
        accessUtils.canFeedConnectCreateListing({ appConfig, cookieEnabledServices });

    const buttonText = active ? 'Deactivate' : 'Publish';
    const { overview: { displayStatus } } = syndication;
    const isDuplicatePaidListingPosted = hasRestrictionErrorMessage && paidListingExists;

    const disablePublishIfDuplicatePaidListingPosted = !isFeed
        && !active
        && isInPaidMarket
        && isDuplicatePaidListingPosted;

    const renderPricingContent = !isActivationBlocked && !disablePublishIfDuplicatePaidListingPosted;

    const isFree = isFeed ? false : !isPaymentRequired;
    const showFeedConnectRate = canFeedConnectCreateListing && singleFamilyContract.isRequired;

    const checkQueryParamActions = () => {
        const { action } = ZRMUtils.queryUtils.parse(search) || {};

        if (action === 'deactivate') {
            handleDeactivateClick();
        }
    };

    const renderGuaranteeIcon = () => (!active && isEligibleForLeadGuarantee)
        && <StyledGuaranteeIcon src={zGuaranteeIcon} />;

    const dispatchGAEvent = (gaEventObj) => {
        const replaceLabelWithAliasEncoded = gaEventObj.label === 'ListingManagement' &&
                                                gaEventObj.category === 'PostingPath';
        const gaEvent = {
            ...gaEventObj,
            ...(replaceLabelWithAliasEncoded) && { label: aliasEncoded },
            ...(isEligibleForLeadGuarantee) && {
                action: appendSuffix(gaEventObj.action, SUFFIX.LEAD_GUARANTEE),
            },
        };

        dispatch(track.GAEventV2(gaEvent));
    };

    const disableLeadGuaranteeEligibility = () => {
        const { leadGuarantee } = currentProperty;

        dispatch(PropertiesActions.updateCurrentProperty({
            leadGuarantee: {
                ...leadGuarantee,
                isEligible: false,
            },
        }));
    };

    const displayFeedListingDeactivateModal = () => dispatch(
        ModalActions.showModal({
            modalType: ModalConstants.FEED_LISTING_DEACTIVATE_MODAL,
            header: 'Deactivate this listing?',
            // cta button text is updated in EditListingPage via updateFormData
            updateFormData,
            // events
            // TODO: get feed listing specific ga events
            // onOpen: () => dispatchGAEvent(GA_EVENTS.DEACTIVATE_BEGIN),
            // onClose: () => dispatchGAEvent(GA_EVENTS.DEACTIVATE_DISMISSED),
        })
    );

    const deactivateListing = () => {
        const syndicationCopy = {
            ...syndication,
            overview: {
                status: SYNDICATION_OVERVIEW_STATUS.INACTIVE,
                displayStatus: LISTING_STATUS.INACTIVE,
                publishedCount: 0,
            },
        };

        dispatch(PostingActions.property.deactivate(aliasEncoded)).then((result) => {
            if (result.success === true) {
                if (canAccessPayments) {
                    dispatch(ModalActions.showModal({
                        modalType: ModalConstants.POST_DEACTIVATION_MODAL,
                        onOpen: () => dispatchGAEvent(GA_EVENTS.POST_DEACTIVATION_MODAL_DISPLAY),
                        onClose: () => dispatchGAEvent(GA_EVENTS.POST_DEACTIVATION_MODAL_DISMISS),
                        isFluid: true,
                    }));
                }

                if (isEligibleForLeadGuarantee) {
                    disableLeadGuaranteeEligibility();
                }
            }
        });
        dispatch(PropertiesActions.updateCurrentProperty({ active: false }));
        updateFormData({
            active: false,
            syndication: syndicationCopy,
            expirationDateTimestamp: Date.now(),
        });
    };

    const handleDeactivateClick = () => {
        const formattedExpirationDate = format(new Date(expirationDate), 'MMM D, YYYY');

        if (currentProperty.isFeed) {
            return displayFeedListingDeactivateModal();
        }

        if (isFree) {
            return deactivateListing();
        }

        return dispatch(
            ModalActions.showModal({
                modalType: ModalConstants.PAID_INCLUSION_DEACTIVATION_MODAL,
                onCancelClick: () => dispatch(ModalActions.hideModal()),
                onGotItClick: () => dispatch(ModalActions.hideModal()),
                onDeactivate: (when) => {
                    if (when === DEACTIVATE_OPTION.NOW) {
                        dispatch(PropertiesActions.updateCurrentProperty({ autoBill: false }));
                        dispatchGAEvent(GA_EVENTS.DEACTIVATE_NOW);
                        deactivateListing();
                    } else {
                        dispatchGAEvent(GA_EVENTS.DEACTIVATE_SCHEDULE);
                        dispatch(PostingActions.property.autoBill(aliasEncoded));
                    }
                },
                expirationDate: formattedExpirationDate,
                autoBillEnabled: currentProperty.autoBill,
                isEligibleForLeadGuarantee,
                onOpen: () => dispatchGAEvent(GA_EVENTS.DEACTIVATE_BEGIN),
                onClose: () => dispatchGAEvent(GA_EVENTS.DEACTIVATE_DISMISSED),
                header: 'Deactivate listing',
                isFluid: true,
            })
        );
    };

    return (
        <>
            <div ref={isVisibleRef} />
            <StickyListingFooterContainer
                data-test="StickyListingFooterContainer"
                isVisible={isVisible}
            >
                <Container>
                    <Flex
                        display="flex"
                        justifyContent="space-between"
                    >
                        <StyledFlexContainer>
                            {renderPricingContent ? (
                                <>
                                    <StyledTagSpacer marginRight="xs">
                                        <Tag
                                            appearance={getTagTypeByKey(STATUS_KEY.LISTING_SUMMARY, displayStatus)}
                                        >
                                            {displayStatus}
                                        </Tag>
                                    </StyledTagSpacer>
                                    {renderGuaranteeIcon()}
                                    <StyledMiddleFlex>
                                        <LearnMore
                                            isInPaidMarket={isInPaidMarket}
                                            isSingleFamilyEnterpriseListing={singleFamilyContract.isRequired}
                                            syndication={syndication}
                                            seattleHousingConnectorStatus={seattleHousingConnectorStatus}
                                        />
                                        {showFeedConnectRate ? (
                                            <FeedConnectAdditionalInformation
                                                active={active}
                                                isMobile={isScreenMobile}
                                                price={singleFamilyContract.details.unitCost}
                                                unitOfMeasure={singleFamilyContract.details.unitOfMeasure}
                                                hidePrice={!singleFamilyContract.isActive}
                                                isCommunity={isCommunity}
                                                syndication={syndication}
                                            />
                                        ) : (
                                            <AdditionalInformation
                                                active={active}
                                                isFree={isFree}
                                                isInPaidMarket={isInPaidMarket}
                                                isMobile={isScreenMobile}
                                                isPaidMultifamily={isPaidMultifamily}
                                                hidePrice={isFeed === true}
                                                isScreenTablet={isScreenTablet}
                                                price={price}
                                                syndication={syndication}
                                                propertyType={propertyType}
                                            />
                                        )}
                                    </StyledMiddleFlex>
                                </>
                            ) : (
                                <ErrorMessage
                                    isActive={active}
                                    // eslint-disable-next-line max-len
                                    disablePublishIfDuplicatePaidListingPosted={disablePublishIfDuplicatePaidListingPosted}
                                />
                            )}
                        </StyledFlexContainer>
                        <Button
                            data-test="StatusButton"
                            buttonType="primary"
                            disabled={!canUpdateActiveStatus}
                            onClick={() => (active ? handleDeactivateClick() : onPublishClick(isFree))}
                        >
                            {isScreenMobile || isScreenTablet ? buttonText : `${buttonText} Listing`}
                        </Button>
                    </Flex>
                </Container>
            </StickyListingFooterContainer>
        </>
    );
};

ConnectedListingFooter.propTypes = propTypes;
ConnectedListingFooter.defaultProps = defaultProps;

export default ConnectedListingFooter;
